import { mdiSunWirelessOutline, mdiWaterThermometerOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { RadialGauge, RadialGaugeOptions } from 'canvas-gauges';
import React, { useEffect, useRef } from 'react';

interface AmbientData {
  [key: string]: any; // Allows for other fields like temp, humidity, etc.
}

interface GaugeTempProps {
  size?: number;
  data: AmbientData;
}

export const GaugeTemp: React.FC<GaugeTempProps> = ({ size = 300, data }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gaugeRef = useRef<RadialGauge | null>(null);

  // Convert Fahrenheit to Celsius as a number for the gauge needle
  const currentCelsius = f2c(data.tempf);

  useEffect(() => {
    if (!canvasRef.current) return;

    const options: RadialGaugeOptions = {
      renderTo: canvasRef.current,
      width: size,
      height: size,
      minValue: -20,
      maxValue: 50,
      majorTicks: ["-20", "-10", "0", "10", "20", "30", "40", "50"],
      minorTicks: 10,
      ticksAngle: 270,
      startAngle: 45,
      strokeTicks: false,
      highlights: [
        { from: -20, to: 0, color: 'rgba(0,0,255,.15)' }, // Light blue for freezing
        { from: 30, to: 50, color: 'rgba(255,0,0,.15)' }  // Light red for hot
      ],
      colorPlate: "#fff",
      colorMajorTicks: "#000",
      colorMinorTicks: "#777",
      colorNumbers: "#000",
      colorNeedle: "red",
      colorNeedleEnd: "red",
      valueBox: false,
      needleType: "line",
      needleStart: 75,
      needleEnd: 99,
      needleWidth: 3,
      borders: true,
      borderOuterWidth: 0,
      colorBorderOuter: "#ddd",
      animationRule: "linear",
      animationDuration: 1000,
      value: f2c(data.tempf || 0),
      needleCircleSize: 1,

    };

    gaugeRef.current = new RadialGauge(options).draw();
    return () => gaugeRef.current?.destroy();
  }, [size]);

  // Update needle when temperature changes
  useEffect(() => {
    if (gaugeRef.current) {
      gaugeRef.current.value = f2c(data.tempf || 0);
    }
  }, [currentCelsius]);

  return (
    <div style={{
      position: 'relative',
      width: size,
      height: size,
      margin: '0 auto'
    }}>
      <canvas ref={canvasRef} />

      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', fontFamily: 'Arial, sans-serif', }}>
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
          <div style={{ fontSize: `${size * 0.12}px`, fontWeight: 'bold', letterSpacing: "-1px", color: '#000' }}>
            {f2c(data.tempf || 0)}°
          </div>

          <div style={{ fontSize: `${size * 0.05}px`, color: '#666', textTransform: 'uppercase', marginTop: '-2px' }}>
            Celcius
          </div>
          <div style={{ fontSize: `${size * 0.07}px`, color: '#444', fontWeight: '600', letterSpacing: '0.5px' }}>

            <Icon path={mdiWaterThermometerOutline} size={size * 0.0035} />
            {f2c(data.dewPoint) || 0}°
          </div>
          <div style={{ fontSize: `${size * 0.07}px`, color: '#444', fontWeight: '600', letterSpacing: '0.5px', marginTop: `-${size * 0.02}px` }}>

            <Icon path={mdiSunWirelessOutline} size={size * 0.0035} />
            {f2c(data.feelsLike)}°
          </div>
        </div>

      </div>
    </div>
  );
};

export default GaugeTemp;

function f2c(deg: number): number {
  return parseFloat(((deg - 32) * 5 / 9).toFixed(1));
}